'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import { compose } from 'redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as customWidgetsSelectors from 'DesignData/selectors/customWidgets';
import { CUSTOM_WIDGET as CUSTOM_WIDGET_FOLDER_TYPE } from 'DesignData/constants/FolderAssetTypes';
import { CUSTOM_WIDGET } from 'DesignData/enums/DesignAssetTypes';
import { getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import { makeGetCustomWidgetParentThemeFolderId } from 'DesignData/selectors/customWidgets';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import { getIsProjectsFile } from 'DesignData/selectors/finder';
import CodeAlert from 'DesignEditorsUI/components/shared/CodeAlert';
import LockedAlert from 'DesignEditorsUI/components/shared/LockedAlert';
import ProjectsFileAlert from 'DesignEditorsUI/components/shared/ProjectsFileAlert';
import ReadOnlyAlert from 'DesignEditorsUI/components/shared/ReadOnlyAlert';
import ReadOnlyPermissionAlert from 'DesignEditorsUI/components/shared/ReadOnlyPermissionAlert';
import withUpgradeModals from 'DesignEditorsUI/components/modals/hoc/withUpgradeModals';
import ReadOnlyThemeAlert from 'DesignEditorsUI/components/shared/ReadOnlyThemeAlert';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.codePanesAlert.${key}`;

function makeMapStateToProps() {
  const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
  const getUpgradeNotPublished = customWidgetBuffersSelectors.makeGetUpgradeNotPublished();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return (state, props) => {
    const {
      name = '',
      default: isDefault,
      moduleId,
      containsOverridableHublTags = false,
      folderId
    } = getCustomWidgetBuffer(state, props) || {};
    const customWidgetsByType = customWidgetsSelectors.getCustomWidgetsByType(state);
    const blogContentModuleId = customWidgetsByType.blog_content && customWidgetsByType.blog_content.moduleId;
    const getIsInLockedFolder = getIsInLockedFolderFactory(state);
    const getCustomWidgetParentThemeFolderId = makeGetCustomWidgetParentThemeFolderId();
    return {
      customWidgetName: name,
      isUpgradeable: customWidgetBuffersSelectors.getisUpgradeable(state, props),
      isDefault,
      isBlogContent: moduleId === blogContentModuleId,
      containsOverridableHublTags,
      upgradeNotPublished: getUpgradeNotPublished(state, props),
      isReadOnly: getIsReadOnly(state, props),
      isProjectsFile: getIsProjectsFile(state, {
        fileId: props.customWidgetId
      }),
      userHasReadAccessOnly: getUserHasReadAccessOnly(state),
      isLocked: getIsInLockedFolder(folderId),
      customWidgetParentThemeFolderId: getCustomWidgetParentThemeFolderId(state, props)
    };
  };
}

class CustomWidgetReadOnlyAlert extends PureComponent {
  renderUpgradeAlert() {
    const titleText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('titleText.v1Editable')
    });

    const moduleDocumentationLink = /*#__PURE__*/_jsx(UILink, {
      target: "_blank",
      href: "http://designers.hubspot.com/docs/modules/reference",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('moduleDocumentationLink')
      })
    });

    const upgradeButton = /*#__PURE__*/_jsx(UIButton, {
      onClick: this.props.onUpgradeClick,
      size: "extra-small",
      use: "tertiary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('upgradeButtonText')
      })
    });

    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(UIAlert, {
        titleText: titleText,
        type: "info",
        children: /*#__PURE__*/_jsxs(UIMediaObject, {
          align: "center",
          itemRight: upgradeButton,
          children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
            message: i18nKey('message.v1Editable'),
            options: {
              moduleDocumentationLink
            }
          }), this.props.UpgradeModal, this.props.ForceUpgradeModal]
        })
      })
    });
  }

  renderUpgradeNotPublishedAlert() {
    const titleText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('titleText.upgradeNotPublished')
    });

    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(UIAlert, {
        titleText: titleText,
        type: "info",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('message.upgradeNotPublished')
        })
      })
    });
  }

  renderLockedAlert() {
    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(LockedAlert, {
        assetType: CUSTOM_WIDGET
      })
    });
  }

  renderProjectsFileAlert() {
    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(ProjectsFileAlert, {
        assetType: CUSTOM_WIDGET
      })
    });
  }

  renderReadOnlyAlert() {
    let message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('message.generic')
    });

    let typeProp = {};

    if (this.props.isDefault) {
      message = /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('message.default')
      });
    }

    if (this.props.isBlogContent) {
      message = /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('message.blogContent')
      });
    }

    if (this.props.containsOverridableHublTags && !this.props.isDefault) {
      const nestedModulesLink = /*#__PURE__*/_jsx(UILink, {
        target: "_blank",
        href: "https://knowledge.hubspot.com/articles/kcs_article/cos-general/how-to-identify-and-upgrade-nested-modules-from-v1-to-v2-in-the-new-design-manager",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('nestedModulesLink')
        })
      });

      message = /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('message.containsOverridableHublTags'),
        options: {
          nestedModulesLink
        }
      });
      typeProp = {
        type: 'warning'
      };
    }

    const titleText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('titleText.readOnly')
    });

    const buttonText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('buttonText')
    });

    const modalHeader = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('cloneModal.header')
    });

    const modalMessage = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('cloneModal.message')
    });

    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(ReadOnlyAlert, Object.assign({
        titleText: titleText,
        message: message,
        buttonText: buttonText,
        showCloneButton: !this.props.isBlogContent,
        modalHeader: modalHeader,
        modalMessage: modalMessage,
        fileId: this.props.customWidgetId,
        fileName: this.props.customWidgetName,
        fileType: CUSTOM_WIDGET_FOLDER_TYPE
      }, typeProp))
    });
  }

  renderReadOnlyThemeAlert() {
    return /*#__PURE__*/_jsx(CodeAlert, {
      children: /*#__PURE__*/_jsx(ReadOnlyThemeAlert, {
        assetId: this.props.customWidgetId
      })
    });
  }

  render() {
    const {
      revisionId,
      upgradeNotPublished,
      userHasReadAccessOnly,
      isUpgradeable,
      isReadOnly,
      isLocked,
      isProjectsFile,
      customWidgetParentThemeFolderId
    } = this.props;

    if (revisionId) {
      return null;
    }

    if (userHasReadAccessOnly) {
      return /*#__PURE__*/_jsx(CodeAlert, {
        children: /*#__PURE__*/_jsx(ReadOnlyPermissionAlert, {
          isReadOnly: isReadOnly
        })
      });
    }

    if (isLocked) {
      return this.renderLockedAlert();
    }

    if (isProjectsFile) {
      return this.renderProjectsFileAlert();
    }

    if (upgradeNotPublished) {
      return this.renderUpgradeNotPublishedAlert();
    }

    if (isUpgradeable) {
      return this.renderUpgradeAlert();
    } else if (isReadOnly && customWidgetParentThemeFolderId && !isLocked) {
      return this.renderReadOnlyThemeAlert();
    } else if (isReadOnly) {
      return this.renderReadOnlyAlert();
    }

    return null;
  }

}

CustomWidgetReadOnlyAlert.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  revisionId: PropTypes.number,
  // @connect
  customWidgetName: PropTypes.string.isRequired,
  isUpgradeable: PropTypes.bool.isRequired,
  isDefault: PropTypes.bool.isRequired,
  isBlogContent: PropTypes.bool,
  containsOverridableHublTags: PropTypes.bool.isRequired,
  upgradeNotPublished: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isProjectsFile: PropTypes.bool,
  userHasReadAccessOnly: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool.isRequired,
  customWidgetParentThemeFolderId: PropTypes.number,
  // @withUpgradeModals
  onUpgradeClick: PropTypes.func.isRequired,
  UpgradeModal: PropTypes.node,
  ForceUpgradeModal: PropTypes.node
};
export default compose(connect(makeMapStateToProps, {}), withUpgradeModals)(CustomWidgetReadOnlyAlert);